import type { EntityId } from './store-config';
import { entityById, isVatRegisteredAt } from './entity-config';

// The four hard guards of CONSOLE-BUILD-SPEC W3. Each is a REFUSAL, not a
// warning: a row that trips one must not be saved, printed or archived. They
// run at every write path (manual form, bulk generation) so a later editing
// mistake in config or a new call site cannot reintroduce the failure the
// compile-time union already prevents.

export interface TaxInvoiceRowCheck {
    entityId: EntityId;
    // Absent = a storeless (offline/manual) row — G4 territory.
    storeId?: string;
    // The seller tax id in the sellerInfo block being written onto the row.
    sellerTaxId: string;
    // The invoice date — the tax point — as 'YYYY-MM-DD'.
    invoiceDateIso: string;
    // Whether the document claims to be a tax invoice at all. Ordinary
    // receipts/delivery notes pass every guard.
    isTaxInvoice: boolean;
}

// Returns the refusal reason, or null when the row may proceed. Guard order
// is deliberate: the pensri refusal is absolute and reads as such even when
// G1 would also have fired.
export const refuseTaxInvoice = (row: TaxInvoiceRowCheck): string | null => {
    // G3 — pensri never issues a tax invoice. Charging VAT while unregistered
    // carries a 2× surcharge plus criminal liability (spec G3). The union
    // type makes the config unrepresentable; this is the runtime belt for
    // config someone may later edit.
    if (row.entityId === 'pensri' && row.isTaxInvoice) {
        return 'G3: pensri ไม่ได้จดทะเบียน VAT และต้องไม่ออกใบกำกับภาษีในทุกกรณี';
    }

    // G1 — no tax invoice dated before the entity's VAT registration. For an
    // unregistered entity (disorn, pensri) this refusal is the CORRECT
    // state, not an error to work around.
    if (
        row.isTaxInvoice &&
        !isVatRegisteredAt(row.entityId, row.invoiceDateIso)
    ) {
        return `G1: ${row.entityId} ยังไม่เป็นผู้ประกอบการจดทะเบียน VAT ณ วันที่ ${row.invoiceDateIso} — ออกใบกำกับภาษีไม่ได้`;
    }

    // G2 — the row's entityId and the seller block written onto it must name
    // the same taxpayer. The row records what was printed; a mismatch means
    // one of them is lying.
    if (row.sellerTaxId !== entityById(row.entityId).taxId) {
        return `G2: sellerInfo.taxId บนเอกสารไม่ตรงกับ tax id ของ ${row.entityId}`;
    }

    // G4 — offline (storeless) sales are genlogic-co only (spec §4.4.1).
    // Constant by rule, so any other entity on a storeless row is a bug.
    if (!row.storeId && row.entityId !== 'genlogic-co') {
        return `G4: รายการขายหน้าร้าน (ไม่มี store) ต้องเป็นของ genlogic-co เท่านั้น ไม่ใช่ ${row.entityId}`;
    }

    return null;
};

// W13 pre-issue validation for a FULL tax invoice (ม.86/4): the buyer's name,
// address, 13-digit tax id and head-office/branch indicator must all appear.
// Severity is WARN (user decision 2026-08-09) — the caller surfaces these but
// proceeds; tighten to a refusal later if data quality allows.
export const validateFullTaxInvoice = (customer: {
    name: string;
    address: string;
    taxId: string;
    branch: string;
}): string[] => {
    const warnings: string[] = [];
    if (!customer.name.trim()) {
        warnings.push('ใบกำกับภาษีเต็มรูปไม่มีชื่อผู้ซื้อ');
    }
    if (!customer.address.trim()) {
        warnings.push('ใบกำกับภาษีเต็มรูปไม่มีที่อยู่ผู้ซื้อ');
    }
    const digits = customer.taxId.replace(/[^0-9]/g, '');
    if (!digits) {
        warnings.push(
            'ใบกำกับภาษีเต็มรูปไม่มีเลขประจำตัวผู้เสียภาษีของผู้ซื้อ',
        );
    } else if (digits.length !== 13) {
        warnings.push(
            `เลขประจำตัวผู้เสียภาษีของผู้ซื้อไม่ครบ 13 หลัก (${digits.length} หลัก)`,
        );
    }
    if (!customer.branch.trim()) {
        warnings.push('ใบกำกับภาษีเต็มรูปไม่ระบุสำนักงานใหญ่/สาขาของผู้ซื้อ');
    }
    return warnings;
};
